import React, { useEffect, useRef, useState } from "react";
import classnames from "classnames";
import { useSearchParams } from "next/navigation";
import { parse } from "qs";

import getBrandPlacement, { Placement } from "@/api/requests/getBrandPlacement";
import { Dispensary } from "@/custom-types/Dispensary";
import { MenuItem } from "@/custom-types/MenuItem";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useSelector } from "@/redux/hooks";
import { getUserPrefersMedical } from "@/redux/selectors/user";
import isDispensaryDualLicense from "@/utils/dispensary/isDispensaryDualLicense";
import { MenuType } from "@/utils/menuTypeUtils";

import Carousel from "@/components/botanic/Carousel";
import MenuCard from "@/components/Dispensary/MenuCard";
import TrackImpression from "@/components/TrackImpression";

type CarouselProps = {
  brand: Placement["brand"];
  cardsPerSlide?: number;
  menuItems: Placement["menuItems"];
  subtitle?: string;
  title?: React.ReactNode;
};

const PromotedBrandCarousel: React.FC<{
  dispensary: Dispensary;
  brandPlacement: Placement | null;
  cardsPerSlide?: number;
  className?: string;
}> = ({ brandPlacement, cardsPerSlide = 4, className, dispensary }) => {
  const searchParams = useSearchParams();
  const queryString = searchParams?.toString() || "";
  const parsedQuery = parse(queryString);
  const isDualLicenseDispensary = isDispensaryDualLicense(dispensary.tags);
  const userPrefersMedical = useSelector(getUserPrefersMedical);

  const [placement, setPlacement] = useState<Placement | null>(brandPlacement);

  const menuType = isDualLicenseDispensary
    ? userPrefersMedical
      ? MenuType.Med
      : MenuType.Rec
    : undefined;
  const isMounted = useRef(false);

  useEffect(() => {
    const fetchPlacement = async () => {
      if (isMounted.current) {
        const brandPlacement = await getBrandPlacement(dispensary.slug, {
          menuType,
          ...parsedQuery,
          strategies: ["lite-amplification", "menu-merchandising"],
        });
        setPlacement(brandPlacement);
      }
      isMounted.current = true;
    };

    fetchPlacement();
  }, [menuType, queryString]);

  /**
   * For now, we'll wait to display anything until there is confirmation
   * that there is a brand to promote. This will cause a reflow that
   * potentially incurs a Cumulative Layout Shift penalty for pages that
   * include this component. This decision should be reassessed at the
   * point where there are enough brands that the hit/miss ratio for showing
   * a promoted brand leans more heavily towards hit.
   */
  if (!placement) {
    return null;
  }

  const { brand, menuItems } = placement;

  const title = `Shop ${brand.name} at ${dispensary.name}`;
  const subtitle = `Sponsored by ${brand.name}`;

  return (
    <div className={classnames(className)}>
      {brand.promotedVia === "menu-merchandising" ? (
        <MenuMerchandisingCarousel
          brand={brand}
          cardsPerSlide={cardsPerSlide}
          dispensary={dispensary}
          menuItems={menuItems}
          subtitle={subtitle}
          title={<h2 className="heading--m font-extrabold">{title}</h2>}
        />
      ) : (
        <LiteAmplificationCarousel
          brand={brand}
          cardsPerSlide={cardsPerSlide}
          menuItems={menuItems}
          subtitle={subtitle}
          title={<h2 className="heading--m font-extrabold">{title}</h2>}
        />
      )}
    </div>
  );
};

const LiteAmplificationCarousel: React.FC<CarouselProps> = ({
  brand,
  cardsPerSlide,
  menuItems,
  subtitle,
  title,
}) => {
  const { publishEvent } = useEventTracker();
  const trackingLabel = `Sponsored Products Carousel - ${brand.name}`;

  const onClick = () =>
    publishEvent({
      action: "Click",
      // @ts-expect-error -- TODO: Fix publishEvent types (https://leafly.atlassian.net/browse/SHH-1133)
      brandId: brand.id,
      category: "Dispensary Menu",
      label: trackingLabel,
    });

  return (
    <TrackImpression
      category="Dispensary Menu"
      action="impression"
      customDimensions={{ brandId: brand.id }}
      label={trackingLabel}
    >
      <Carousel cardsPerSlide={cardsPerSlide} subtitle={subtitle} title={title}>
        {menuItems.map((menuItem: MenuItem) => (
          <MenuCard
            dispensaryId={menuItem.dispensary.id}
            key={menuItem.id}
            menuItem={menuItem}
            onClick={onClick}
          />
        ))}
      </Carousel>
    </TrackImpression>
  );
};

const CARD_TRACKING = {
  action: "impression",
  category: "Sponsored Menu Item Card",
  label: "Menu Merch Card",
};

const MenuMerchandisingCarousel: React.FC<
  CarouselProps & { dispensary: Dispensary }
> = ({ brand, cardsPerSlide, dispensary, menuItems, subtitle, title }) => {
  const { publishEvent } = useEventTracker();
  const carouselTracking = {
    category: "Sponsored Menu Merch Carousel",

    customDimensions: {
      brandId: brand.id,
      dispensaryId: dispensary.id,
    },
    /**
     * This label will be tracked by BI to determine how many cards show
     * up in Menu Merchandising carousels.
     */
    label: menuItems.length,
  };

  useEffect(() => {
    /**
     * Menu Merchandising is sold based on pageviews at the time of this
     * writing (2021/28/10). Given that the brands purchasing this addon
     * expect a certain lift based on pageviews rather than visible
     * impressions, we're tracking impressions on this carousel regardless
     * of whether the element is actually visible to the consumer.
     */
    publishEvent({
      action: "Impression",
      category: carouselTracking.category,
      label: String(carouselTracking.label),
      ...carouselTracking.customDimensions,
    });
  }, [menuItems]);

  return (
    <Carousel cardsPerSlide={cardsPerSlide} title={title} subtitle={subtitle}>
      {menuItems.map((menuItem: MenuItem, i: number) => {
        const trackingDimensions = {
          ...carouselTracking.customDimensions,
          carouselSlot: i + 1,
          menuItemId: menuItem.menuItemId,
          merchandisingCampaignId: brand.campaignId,
          productId: menuItem.product?.id,
          strainId: menuItem.strain?.id,
        };

        return (
          <TrackImpression
            key={menuItem.id}
            customDimensions={trackingDimensions}
            className="h-full"
            {...CARD_TRACKING}
          >
            <MenuCard
              dispensaryId={menuItem.dispensary.id}
              menuItem={menuItem}
              campaignId={brand.campaignId}
              onClick={() => {
                publishEvent({
                  action: "click",
                  category: CARD_TRACKING.category,
                  label: CARD_TRACKING.label,
                  ...trackingDimensions,
                });
              }}
              onAddToBagClick={() => {
                publishEvent({
                  action: "click",
                  category: CARD_TRACKING.category,
                  label: "Menu Merch ATB Button",
                  ...trackingDimensions,
                });
              }}
            />
          </TrackImpression>
        );
      })}
    </Carousel>
  );
};

export default PromotedBrandCarousel;
